import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Box,
  Divider,
  useTheme,
  useMediaQuery,
  IconButton,
  Button,
  CircularProgress,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RateReviewIcon from '@mui/icons-material/RateReview';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import HistoryIcon from '@mui/icons-material/History';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useAuth } from '../hooks/useAuth';

const DRAWER_WIDTH = 240;
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

interface SidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', path: '/app/dashboard', icon: <DashboardIcon /> },
  { label: 'New Proposal', path: '/app/generate', icon: <AddCircleOutlineIcon /> },
  { label: 'Review Proposal', path: '/app/review', icon: <RateReviewIcon /> },
  { label: 'Context', path: '/app/context', icon: <FolderOpenIcon /> },
  { label: 'History', path: '/app/history', icon: <HistoryIcon /> },
  { label: 'Settings', path: '/app/settings', icon: <SettingsIcon /> },
];

export default function Sidebar({ mobileOpen = false, onMobileClose }: SidebarProps) {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isGenerating, setIsGenerating] = useState(false);

  // Listen for generation status changes
  useEffect(() => {
    const newSocket = io(API_BASE_URL);

    newSocket.on('proposal-progress', (data: { status: string }) => {
      if (data.status === 'complete' || data.status === 'error') {
        setIsGenerating(false);
      } else {
        setIsGenerating(true);
      }
    });

    newSocket.on('proposal-complete', () => {
      setIsGenerating(false);
    });

    newSocket.on('proposal-error', () => {
      setIsGenerating(false);
    });

    // Check localStorage for active generation on mount
    const savedState = localStorage.getItem('proposalGenerationState');
    if (savedState) {
      try {
        const state = JSON.parse(savedState);
        if (state.progress && state.progress.status !== 'complete' && state.progress.status !== 'error') {
          setIsGenerating(true);
        }
      } catch (error) {
        console.error('Failed to parse saved state:', error);
      }
    }

    return () => {
      newSocket.close();
    };
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{
        justifyContent: isMobile ? 'space-between' : 'center',
        borderBottom: 1,
        borderColor: 'divider',
      }}>
        <Box sx={{
          fontWeight: 700,
          fontSize: '1.25rem',
          color: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}>
          Grant Proposals
        </Box>
        {isMobile && (
          <IconButton onClick={onMobileClose}>
            <ChevronLeftIcon />
          </IconButton>
        )}
      </Toolbar>

      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        <List>
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            // Show spinner for "New Proposal" when generating
            const icon = item.label === 'New Proposal' && isGenerating
              ? <CircularProgress size={24} sx={{ color: isActive ? 'inherit' : 'primary.main' }} />
              : item.icon;

            return (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  component={Link}
                  to={item.path}
                  onClick={isMobile ? onMobileClose : undefined}
                  selected={isActive}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                      '& .MuiListItemIcon-root': {
                        color: 'primary.contrastText',
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: isActive ? 'inherit' : 'text.secondary' }}>
                    {icon}
                  </ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>

      <Divider />

      {user && (
        <Box sx={{ p: 2 }}>
          <Box sx={{ mb: 1, px: 1, fontSize: '0.875rem', color: 'text.secondary' }}>
            {user.email}
          </Box>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{ borderRadius: 1 }}
          >
            Logout
          </Button>
        </Box>
      )}

      <Box sx={{ p: 2, textAlign: 'center', fontSize: '0.75rem', color: 'text.secondary' }}>
        Powered by Claude AI
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile drawer */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={onMobileClose}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        /* Desktop drawer */
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </>
  );
}
